/* eslint-disable no-duplicate-imports */
import { useCallback, useEffect } from 'react';
import { EMPTY_OBJECT } from '@app/consts/common_consts';
import { yupResolver } from '@hookform/resolvers/yup';
import type { FieldValues } from 'react-hook-form';
import { useForm as useBaseHookForm } from 'react-hook-form';
import * as yup from 'yup';
import { getFieldDisabledHandler, setFieldDisabledHandler } from './handlers/fieldDisabledHandler';
import { handlerSubmitHandler } from './handlers/handlerSubmitHandler';
import { makeFormOptionsOverride } from './handlers/makeFormOptionsOverride';
import { setErrorsHanlder } from './handlers/setErrorsHanlder';
import { setFieldArrayHanlder } from './handlers/setFieldArrayHanlder';
import { setValuesHanlder } from './handlers/setValuesHanlder';
import { disabledFieldsSignify, FORM_NAME_DEFAULT } from './index.store';
import { HookFormCustomParams, HookFormCustomReturn } from './types';

const defaultSchema = yup.object().shape({});

const useForm = <TFieldValues extends FieldValues = FieldValues, TContext = ControlledType>(
  props: HookFormCustomParams<TFieldValues, TContext>
): HookFormCustomReturn<TFieldValues, TContext> => {
  const { formName = FORM_NAME_DEFAULT, defaultDisabledFields = EMPTY_OBJECT } = props;
  const disabledFields = disabledFieldsSignify.use();
  const resolver = yupResolver(props.validationSchema || defaultSchema);
  const form = useBaseHookForm<TFieldValues>({
    ...props,
    ...makeFormOptionsOverride(props),
    resolver,
  });

  useEffect(() => {
    // Initialize disabled fields with default values
    if (Object.keys(defaultDisabledFields).length > 0) {
      disabledFieldsSignify.set((pre) => {
        pre.value[formName] = defaultDisabledFields;
      });
    }

    return () => {
      disabledFieldsSignify.set((pre) => {
        pre.value[formName] = EMPTY_OBJECT;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setFieldDisabled = useCallback(setFieldDisabledHandler(formName), [props.formName]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getFieldDisabled = useCallback(getFieldDisabledHandler(disabledFields, formName), [
    disabledFields,
    props.formName,
  ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setValues = useCallback(setValuesHanlder(form), [form]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setErrors = useCallback(setErrorsHanlder(form), [form]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setFieldArray = useCallback(setFieldArrayHanlder(form), [form]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSubmit = useCallback(handlerSubmitHandler(form, props), [
    form,
    props.validationSubmission,
  ]);

  return {
    ...form,
    setFieldArray,
    setErrors,
    setValues,
    handleSubmit,
    setFieldDisabled,
    getFieldDisabled,
  };
};

export { useForm };
